import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { eventsService } from '../../services/eventsService';
import { transactionsService } from '../../services/transactionsService';
import { authService } from '../../services/api';
import { userService } from '../../services/userService';
import { venuesService } from '../../services/venuesService';
import { artistsService } from '../../services/artistsService';
import { cardsService } from '../../services/cardsService';
import { getApiUserCode } from '../Shared/ApiUserBadge';
import HeaderNewTabButton from '../Shared/HeaderNewTabButton';
import './ManualEntryPage.css';
import { APPROVED_CURRENCIES, EVENT_CURRENCIES } from '../Events/eventCurrency';

const locationOptions = ['New_York', 'North_America', 'World', 'Greece', 'London'];
const primaryVendorOptions = [
  { value: 'dice', label: 'Dice' },
  { value: 'aspen', label: 'Aspen' },
  { value: 'pacha', label: 'Pacha' },
  { value: 'ticketmaster', label: 'Ticketmaster' },
  { value: 'axs', label: 'AXS' },
  { value: 'get_in', label: 'Get-In' },
  { value: 'tao', label: 'TAO' },
];
const otherVendorOptions = [
  { value: 'other', label: 'Other' },
  { value: 'tixr', label: 'Tixr' },
  { value: 'seetickets', label: 'Seetickets' },
  { value: 'shotgun', label: 'Shotgun' },
  { value: 'fever', label: 'Fever' },
  { value: 'more', label: 'More' },
  { value: 'ticketservices', label: 'Ticketservices' },
  { value: 'cavo', label: 'Cavo' },
  { value: 'showclix', label: 'Showclix' },
  { value: 'frontgate', label: 'Frontgate' },
  { value: 'ra', label: 'RA' },
];
const allVendorOptions = [...primaryVendorOptions, ...otherVendorOptions];
const fallbackPlatformOptions = [
  'PDF',
  'Mobile Transfer',
  'Apple Wallet',
  'Hard Stock',
  'Tao App',
];
const orderSalePointOptions = ['Other', 'Stubhub', 'Stubhub LND', 'Stubhub EU', 'Vivid Seats', 'Lysted', 'Skybox', 'Ticketmaster', 'AXS', 'Ticketswap'];
const orderStatusOptions = ['Order Complete', 'Dice Pending', 'RTT', 'Transfer Now', 'Price', 'Dice Check', 'Delayed', 'To Buy', 'Void', 'Sold', 'Retransfer', 'Cancelled'];
const vendorPlatformOptions = {
  dice: ['Dice'],
  aspen: ['Belly Up', 'Will Call'],
  pacha: ['PDF'],
  get_in: ['Mobile Transfer'],
  ticketmaster: ['Mobile Transfer'],
  axs: ['Mobile Transfer'],
  tao: ['PDF', 'Tao App'],
  tixr: ['PDF', 'Mobile Transfer'],
  seetickets: ['PDF'],
  shotgun: ['PDF', 'Mobile Transfer'],
  fever: ['PDF'],
  more: ['PDF'],
  ticketservices: ['PDF'],
  cavo: ['Apple Wallet'],
  showclix: ['PDF'],
  frontgate: ['PDF', 'Hard Stock'],
  other: fallbackPlatformOptions,
};
const acceptedTicketTypes = [
  'GA',
  'Anytime',
  'Before 1am',
  'Before 3am',
  'MALE GA',
  'FEMALE GA',
  '3-Day GA',
  '2-Day GA',
  'VIP',
  'Parking',
  'Backstage',
  'VOID',
  'Season',
];
const ASPEN_DEFAULT_VENUE = 'Belly Up';

const initialForm = {
  sale_point: 'Other',
  order_id: '',
  order_status: 'Price',
  location: '',
  currency: '',
  vendor: '',
  artist: '',
  artist_id: '',
  event_name: '',
  event_date: '',
  ticket_type: '',
  quantity: '1',
  amount_total: '',
  unit_price: '',
  venue: '',
  venue_id: '',
  ticket_platform: '',
  operator: '',
  notes: '',
  override_primary_card: '',
};
const emptyVenueForm = {
  name: '',
  location: '',
  country: '',
  capacity: '',
  twz_location: '',
};
const emptyCardForm = {
  card_name: '',
  owner_username: '',
  status: '',
  is_zen_privacy: false,
  expiry: '',
  ccv: '',
};

const formatMoney = (value) => {
  if (!Number.isFinite(value)) return '';
  return value.toFixed(2);
};

const isValidNativeDate = (value) => {
  if (!value) return true;
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
};

const parseNativeDate = (value) => {
  if (!value || !isValidNativeDate(value)) return null;
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const isAllowedManualEntryDate = (value) => {
  if (!value) return true;
  const entryDate = parseNativeDate(value);
  if (!entryDate) return false;

  const yesterday = new Date();
  yesterday.setHours(0, 0, 0, 0);
  yesterday.setDate(yesterday.getDate() - 1);

  return entryDate >= yesterday;
};

const toDisplayDate = (value) => {
  if (!value) return null;
  const [year, month, day] = value.split('-');
  return `${day}/${month}/${year}`;
};

const toEventRef = (sourceUuid) => {
  if (!sourceUuid) return '';
  return String(sourceUuid).replace(/[A-Za-z]+$/, '');
};

const getPlatformOptionsForVendor = (vendor) => (
  vendorPlatformOptions[vendor] || fallbackPlatformOptions
);

const getNextPlatformForVendor = (vendor, currentPlatform) => {
  const options = getPlatformOptionsForVendor(vendor);
  if (options.includes(currentPlatform)) return currentPlatform;
  return options.length === 1 ? options[0] : '';
};

const vendorSearchKey = (vendor) => (
  ['dice', 'aspen', 'pacha', 'ticketmaster', 'get_in', 'seetickets', 'axs', 'tao', 'tixr'].includes(vendor) ? vendor : null
);

const ManualEntryPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const origin = queryParams.get('origin') || 'dashboard';
  const returnTo = queryParams.get('returnTo') || (origin === 'events' ? '/user/events' : '/user');
  const requestedEntryType = queryParams.get('type') === 'order' ? 'order' : 'purchase';
  const initialEntryType = requestedEntryType;

  const [entryType, setEntryType] = useState(initialEntryType);
  const [form, setForm] = useState(initialForm);
  const currency = form.location === 'World'
    ? (form.currency || EVENT_CURRENCIES.World)
    : (EVENT_CURRENCIES[form.location] || '');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userQuery, setUserQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [artistSuggestions, setArtistSuggestions] = useState([]);
  const [artistSuggestionsOpen, setArtistSuggestionsOpen] = useState(false);
  const [artistSuggestionsLoading, setArtistSuggestionsLoading] = useState(false);
  const [showCreateArtistModal, setShowCreateArtistModal] = useState(false);
  const [createArtistName, setCreateArtistName] = useState('');
  const [createArtistSaving, setCreateArtistSaving] = useState(false);
  const [createArtistError, setCreateArtistError] = useState('');
  const [venueSuggestions, setVenueSuggestions] = useState([]);
  const [venueSuggestionsOpen, setVenueSuggestionsOpen] = useState(false);
  const [venueSuggestionsLoading, setVenueSuggestionsLoading] = useState(false);
  const [cardSuggestions, setCardSuggestions] = useState([]);
  const [cardSuggestionsOpen, setCardSuggestionsOpen] = useState(false);
  const [cardSuggestionsLoading, setCardSuggestionsLoading] = useState(false);
  const [selectedOverrideCard, setSelectedOverrideCard] = useState(null);
  const [showCardRequiredModal, setShowCardRequiredModal] = useState(false);
  const [requiredCardQuery, setRequiredCardQuery] = useState('');
  const [requiredCardSuggestions, setRequiredCardSuggestions] = useState([]);
  const [requiredCardSuggestionsLoading, setRequiredCardSuggestionsLoading] = useState(false);
  const [selectedRequiredCard, setSelectedRequiredCard] = useState(null);
  const [saveRequiredCardToAccount, setSaveRequiredCardToAccount] = useState(false);
  const [cardRequiredDetails, setCardRequiredDetails] = useState(null);
  const [showCreateCardModal, setShowCreateCardModal] = useState(false);
  const [createCardContext, setCreateCardContext] = useState('override');
  const [createCardForm, setCreateCardForm] = useState(emptyCardForm);
  const [createCardSaving, setCreateCardSaving] = useState(false);
  const [createCardError, setCreateCardError] = useState('');
  const [showCreateVenueModal, setShowCreateVenueModal] = useState(false);
  const [createVenueForm, setCreateVenueForm] = useState(emptyVenueForm);
  const [createVenueSaving, setCreateVenueSaving] = useState(false);
  const [createVenueError, setCreateVenueError] = useState('');
  const [ticketTypeSuggestions, setTicketTypeSuggestions] = useState([]);
  const [ticketTypeSuggestionsOpen, setTicketTypeSuggestionsOpen] = useState(false);
  const skipNextTicketTypeSuggestions = useRef(false);
  const skipNextOverrideCardSuggestions = useRef(false);
  const skipNextRequiredCardSuggestions = useRef(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [savedLinks, setSavedLinks] = useState(null);
  const [error, setError] = useState('');
  const [vendorMenuOpen, setVendorMenuOpen] = useState(false);
  const [otherVendorsOpen, setOtherVendorsOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [advancedOptionsOpen, setAdvancedOptionsOpen] = useState(false);
  const [keepDetails, setKeepDetails] = useState(false);
  const vendorDropdownRef = useRef(null);

  const backLabel = origin === 'events'
    ? 'Back to Events'
    : origin === 'transactions'
      ? (returnTo.includes('/orders') ? 'Back to Orders' : 'Back to Purchases')
      : 'Back to Dashboard';
  const currentPlatformOptions = useMemo(
    () => getPlatformOptionsForVendor(form.vendor),
    [form.vendor],
  );
  const currentVendorSearchKey = useMemo(() => vendorSearchKey(form.vendor), [form.vendor]);
  const selectedVendorLabel = useMemo(
    () => allVendorOptions.find((item) => item.value === form.vendor)?.label || 'Select vendor',
    [form.vendor],
  );
  const canOpenEvents = currentUser && (
    authService.isAdmin(currentUser) || getApiUserCode(currentUser.email) === 'TS'
  );
  const eventsPath = currentUser && authService.isAdmin(currentUser) ? '/dashboard/events' : '/user/events';

  const resetUser = () => {
    setUserQuery('');
    setSelectedUser(null);
    setSuggestions([]);
    setSuggestionsOpen(false);
  };

  useEffect(() => {
    let isMounted = true;
    const token = authService.getToken();
    if (!token) return undefined;

    authService.getCurrentUser(token)
      .then((user) => {
        if (isMounted) {
          setCurrentUser(user);
        }
      })
      .catch(() => {
        if (isMounted) {
          setCurrentUser(null);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!vendorMenuOpen) return undefined;

    const closeVendorMenu = (event) => {
      if (vendorDropdownRef.current && vendorDropdownRef.current.contains(event.target)) {
        return;
      }
      setVendorMenuOpen(false);
      setOtherVendorsOpen(false);
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setVendorMenuOpen(false);
        setOtherVendorsOpen(false);
      }
    };

    document.addEventListener('mousedown', closeVendorMenu);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', closeVendorMenu);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [vendorMenuOpen]);

  useEffect(() => {
    if (skipNextTicketTypeSuggestions.current) {
      skipNextTicketTypeSuggestions.current = false;
      setTicketTypeSuggestions([]);
      setTicketTypeSuggestionsOpen(false);
      return undefined;
    }

    if (entryType === 'void') {
      setTicketTypeSuggestions([]);
      setTicketTypeSuggestionsOpen(false);
      return undefined;
    }

    const query = form.ticket_type.trim();
    if (!query) {
      setTicketTypeSuggestions(acceptedTicketTypes);
      setTicketTypeSuggestionsOpen(false);
      return undefined;
    }

    const normalizedQuery = query.toLowerCase();
    setTicketTypeSuggestions(
      acceptedTicketTypes.filter((item) => item.toLowerCase().includes(normalizedQuery)),
    );
    setTicketTypeSuggestionsOpen(true);
    return undefined;
  }, [form.ticket_type, entryType]);

  useEffect(() => {
    const query = form.artist.trim();
    if (!query || form.artist_id) {
      setArtistSuggestions([]);
      setArtistSuggestionsOpen(false);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setArtistSuggestionsLoading(true);
      const result = await artistsService.listArtists({
        q: query,
        limit: 50,
      });
      setArtistSuggestionsLoading(false);

      if (!result.success) {
        setArtistSuggestions([]);
        setArtistSuggestionsOpen(false);
        return;
      }

      setArtistSuggestions(result.items || []);
      setArtistSuggestionsOpen(true);
    }, 250);

    return () => clearTimeout(timer);
  }, [form.artist, form.artist_id]);

  useEffect(() => {
    const query = form.venue.trim();
    if (form.vendor === 'aspen' && query.toLowerCase() === ASPEN_DEFAULT_VENUE.toLowerCase()) {
      setVenueSuggestions([]);
      setVenueSuggestionsOpen(false);
      return undefined;
    }

    if (!query || form.venue_id) {
      setVenueSuggestions([]);
      setVenueSuggestionsOpen(false);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setVenueSuggestionsLoading(true);
      const result = await venuesService.listVenues({
        q: query,
        limit: 50,
      });
      setVenueSuggestionsLoading(false);

      if (!result.success) {
        setVenueSuggestions([]);
        setVenueSuggestionsOpen(false);
        return;
      }

      const filtered = (result.items || []).filter((venue) => (
        !form.location || venue.twz_location === form.location
      ));
      setVenueSuggestions(filtered);
      setVenueSuggestionsOpen(true);
    }, 250);

    return () => clearTimeout(timer);
  }, [form.venue, form.venue_id, form.location, form.vendor]);

  useEffect(() => {
    if (skipNextOverrideCardSuggestions.current) {
      skipNextOverrideCardSuggestions.current = false;
      return undefined;
    }
    const query = form.override_primary_card.trim();
    if (!query) {
      setCardSuggestions([]);
      setCardSuggestionsOpen(false);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setCardSuggestionsLoading(true);
      const result = await cardsService.listCards({
        q: query,
        limit: 20,
      });
      setCardSuggestionsLoading(false);

      if (!result.success) {
        setCardSuggestions([]);
        setCardSuggestionsOpen(false);
        return;
      }

      setCardSuggestions(result.items || []);
      setCardSuggestionsOpen(true);
    }, 250);

    return () => clearTimeout(timer);
  }, [form.override_primary_card]);

  useEffect(() => {
    if (!showCardRequiredModal) return undefined;
    if (skipNextRequiredCardSuggestions.current) {
      skipNextRequiredCardSuggestions.current = false;
      return undefined;
    }
    const query = requiredCardQuery.trim();
    if (!query) {
      setRequiredCardSuggestions([]);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setRequiredCardSuggestionsLoading(true);
      const result = await cardsService.listCards({ q: query, limit: 20 });
      setRequiredCardSuggestionsLoading(false);
      setRequiredCardSuggestions(result.success ? (result.items || []) : []);
    }, 250);

    return () => clearTimeout(timer);
  }, [requiredCardQuery, showCardRequiredModal]);

  useEffect(() => {
    if (form.vendor !== 'aspen') return undefined;

    setVenueSuggestions([]);
    setVenueSuggestionsOpen(false);

    setForm((prev) => ({
      ...prev,
      venue: ASPEN_DEFAULT_VENUE,
      ticket_platform: ASPEN_DEFAULT_VENUE,
    }));

    let cancelled = false;
    const resolveAspenVenue = async () => {
      const result = await venuesService.listVenues({ q: ASPEN_DEFAULT_VENUE, limit: 20 });
      if (cancelled || !result.success) return;

      const exactVenue = (result.items || []).find(
        (venue) => venue.name?.trim().toLowerCase() === ASPEN_DEFAULT_VENUE.toLowerCase(),
      );
      if (!exactVenue) return;

      setForm((prev) => {
        if (prev.vendor !== 'aspen') return prev;
        return {
          ...prev,
          venue: exactVenue.name,
          venue_id: exactVenue.venue_id,
          location: exactVenue.twz_location || prev.location,
          ticket_platform: ASPEN_DEFAULT_VENUE,
        };
      });
    };

    resolveAspenVenue();

    return () => {
      cancelled = true;
    };
  }, [form.vendor, entryType]);

  const updateField = (field, value) => {
    setMessage('');
    setSavedLinks(null);
    setError('');

    if (field === 'vendor') {
      resetUser();
      setSelectedOverrideCard(null);
    }
    if (field === 'override_primary_card') {
      setSelectedOverrideCard(null);
    }

    setForm((prev) => {
      const next = {
        ...prev,
        [field]: value,
        ...(field === 'artist'
          ? {
              artist_id: '',
              event_name: !prev.event_name.trim() || prev.event_name === prev.artist ? value : prev.event_name,
            }
          : {}),
        ...(field === 'venue' ? { venue_id: '' } : {}),
        ...(field === 'location'
          ? { venue: '', venue_id: '', currency: value === 'World' ? EVENT_CURRENCIES.World : '' }
          : {}),
        ...(field === 'vendor'
          ? { ticket_platform: getNextPlatformForVendor(value, prev.ticket_platform), override_primary_card: '' }
          : {}),
        ...(field === 'vendor' && value === 'aspen'
          ? { venue: ASPEN_DEFAULT_VENUE, venue_id: '' }
          : {}),
      };
      const quantity = Number(field === 'quantity' ? value : next.quantity);

      if (entryType === 'void') {
        return { ...next, quantity: '0', amount_total: '0.00', unit_price: '0.00' };
      }

      if (field === 'amount_total' && quantity > 0 && value !== '') {
        const amount = Number(value);
        if (!Number.isNaN(amount)) {
          next.unit_price = formatMoney(amount / quantity);
        }
      }

      if (field === 'unit_price' && quantity > 0 && value !== '') {
        const unit = Number(value);
        if (!Number.isNaN(unit)) {
          next.amount_total = formatMoney(unit * quantity);
        }
      }

      if (field === 'quantity' && quantity > 0) {
        const unit = Number(next.unit_price);
        const amount = Number(next.amount_total);
        if (next.unit_price !== '' && !Number.isNaN(unit)) {
          next.amount_total = formatMoney(unit * quantity);
        } else if (next.amount_total !== '' && !Number.isNaN(amount)) {
          next.unit_price = formatMoney(amount / quantity);
        }
      }

      return next;
    });
  };

  const selectVendorOption = (value) => {
    updateField('vendor', value);
    setVendorMenuOpen(false);
    setOtherVendorsOpen(false);
  };

  const selectArtist = (artist) => {
    setForm((prev) => ({
      ...prev,
      artist: artist.name,
      artist_id: artist.artist_id,
      event_name: !prev.event_name.trim() || prev.event_name === prev.artist ? artist.name : prev.event_name,
    }));
    setArtistSuggestions([]);
    setArtistSuggestionsOpen(false);
    setMessage('');
    setSavedLinks(null);
    setError('');
  };

  const openCreateArtistModal = () => {
    setCreateArtistName(form.artist.trim());
    setCreateArtistError('');
    setArtistSuggestionsOpen(false);
    setShowCreateArtistModal(true);
  };

  const closeCreateArtistModal = () => {
    if (createArtistSaving) return;
    setShowCreateArtistModal(false);
    setCreateArtistError('');
  };

  const handleCreateArtist = async (event) => {
    event.preventDefault();

    const name = createArtistName.trim();
    if (!name) {
      setCreateArtistError('Artist name is required.');
      return;
    }

    setCreateArtistSaving(true);
    setCreateArtistError('');
    const result = await artistsService.createArtist({ name });
    setCreateArtistSaving(false);

    if (!result.success) {
      setCreateArtistError(result.error);
      return;
    }

    selectArtist(result.item);
    setShowCreateArtistModal(false);
  };

  const selectVenue = (venue) => {
    setForm((prev) => ({
      ...prev,
      venue: venue.name,
      venue_id: venue.venue_id,
      location: venue.twz_location || prev.location,
    }));
    setVenueSuggestions([]);
    setVenueSuggestionsOpen(false);
    setMessage('');
    setSavedLinks(null);
    setError('');
  };

  const openCreateVenueModal = () => {
    setCreateVenueForm({
      ...emptyVenueForm,
      name: form.venue.trim(),
      twz_location: form.location,
    });
    setCreateVenueError('');
    setVenueSuggestionsOpen(false);
    setShowCreateVenueModal(true);
  };

  const closeCreateVenueModal = () => {
    if (createVenueSaving) return;
    setShowCreateVenueModal(false);
    setCreateVenueError('');
  };

  const updateCreateVenueField = (field, value) => {
    setCreateVenueForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCreateVenue = async (event) => {
    event.preventDefault();

    const name = createVenueForm.name.trim();
    const venueLocation = createVenueForm.location.trim();
    const country = createVenueForm.country.trim();

    if (!name || !venueLocation || !country) {
      setCreateVenueError('Venue name, Location, and Country are required.');
      return;
    }

    const capacity = createVenueForm.capacity === '' ? null : Number(createVenueForm.capacity);
    if (capacity != null && (Number.isNaN(capacity) || capacity < 0)) {
      setCreateVenueError('Capacity must be a nonnegative number.');
      return;
    }

    setCreateVenueSaving(true);
    setCreateVenueError('');
    const result = await venuesService.createVenue({
      name,
      location: venueLocation,
      country,
      capacity: capacity == null ? null : Math.trunc(capacity),
      twz_location: createVenueForm.twz_location || null,
    });
    setCreateVenueSaving(false);

    if (!result.success) {
      setCreateVenueError(result.error);
      return;
    }

    selectVenue(result.item);
    setShowCreateVenueModal(false);
  };

  const selectOverrideCard = (card) => {
    skipNextOverrideCardSuggestions.current = true;
    setSelectedOverrideCard(card);
    setForm((prev) => ({ ...prev, override_primary_card: card.card_name || '' }));
    setCardSuggestions([]);
    setCardSuggestionsOpen(false);
    setError('');
  };

  const selectRequiredCard = (card) => {
    skipNextRequiredCardSuggestions.current = true;
    setSelectedRequiredCard(card);
    setRequiredCardQuery(card.card_name || '');
    setRequiredCardSuggestions([]);
    setError('');
  };

  const openCreateCardModal = (context) => {
    const cardName = context === 'required' ? requiredCardQuery.trim() : form.override_primary_card.trim();
    const ownerUsername = currentUser
      ? (getApiUserCode(currentUser.email) || currentUser.username || '')
      : '';
    setCreateCardContext(context);
    setCreateCardForm({ ...emptyCardForm, card_name: cardName, owner_username: ownerUsername });
    setCreateCardError('');
    setCardSuggestionsOpen(false);
    setRequiredCardSuggestions([]);
    setShowCreateCardModal(true);
  };

  const closeCreateCardModal = () => {
    if (createCardSaving) return;
    setShowCreateCardModal(false);
    setCreateCardError('');
  };

  const updateCreateCardField = (field, value) => {
    setCreateCardForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreateCard = async (event) => {
    event.preventDefault();
    const cardName = createCardForm.card_name.trim();
    const ownerUsername = createCardForm.owner_username.trim();
    if (!cardName || !ownerUsername) {
      setCreateCardError('Card Name and Owner are required.');
      return;
    }

    setCreateCardSaving(true);
    setCreateCardError('');
    const result = await cardsService.createCard({
      card_name: cardName,
      owner_username: ownerUsername,
      status: createCardForm.status.trim() || null,
      is_zen_privacy: createCardForm.is_zen_privacy,
      expiry: createCardForm.expiry.trim() || null,
      ccv: createCardForm.ccv.trim() || null,
    });
    setCreateCardSaving(false);

    if (!result.success) {
      setCreateCardError(result.error);
      return;
    }

    if (createCardContext === 'required') {
      selectRequiredCard(result.item);
    } else {
      selectOverrideCard(result.item);
    }
    setShowCreateCardModal(false);
  };

  const switchEntryType = (nextType) => {
    setEntryType(nextType);
    setMessage('');
    setSavedLinks(null);
    setError('');
    setTicketTypeSuggestions([]);
    setTicketTypeSuggestionsOpen(false);
    setForm((prev) => {
      if (nextType === 'void') {
        resetUser();
        return {
          ...prev,
          ...(prev.vendor === 'aspen'
            ? { venue: ASPEN_DEFAULT_VENUE, ticket_platform: ASPEN_DEFAULT_VENUE }
            : {}),
          ticket_type: 'VOID',
          quantity: '0',
          amount_total: '0.00',
          unit_price: '0.00',
        };
      }
      if (nextType === 'order') {
        return {
          ...prev,
          ticket_type: prev.ticket_type === 'VOID' ? '' : prev.ticket_type,
          quantity: prev.quantity === '0' ? '1' : prev.quantity,
          order_status: prev.order_status || 'Price',
          sale_point: prev.sale_point || 'Other',
        };
      }
      return { ...prev, ticket_type: prev.ticket_type === 'VOID' ? '' : prev.ticket_type, quantity: prev.quantity === '0' ? '1' : prev.quantity };
    });
  };

  const handleUserSearch = async (value) => {
    setUserQuery(value);
    setSelectedUser(null);
    setMessage('');
    setSavedLinks(null);

    const query = value.trim();
    if (!query || !form.vendor) {
      setSuggestions([]);
      setSuggestionsOpen(false);
      return;
    }

    setSuggestionsLoading(true);
    const result = await userService.searchVendorUsers(
      query,
      vendorSearchKey(form.vendor),
    );
    if (result.success) {
      setSuggestions(result.items || []);
      setSuggestionsOpen(true);
    } else {
      setSuggestions([]);
      setSuggestionsOpen(false);
    }
    setSuggestionsLoading(false);
  };

  const openTicketTypeSuggestions = async () => {
    if (ticketTypeSuggestionsOpen || saving || entryType === 'void') {
      return;
    }

    const normalizedQuery = form.ticket_type.trim().toLowerCase();
    setTicketTypeSuggestions(
      normalizedQuery
        ? acceptedTicketTypes.filter((item) => item.toLowerCase().includes(normalizedQuery))
        : acceptedTicketTypes,
    );
    setTicketTypeSuggestionsOpen(true);
  };

  const validate = () => {
    if (entryType === 'order' && !form.order_id.trim()) return 'Order ID is required.';
    if (entryType === 'order' && !form.sale_point) return 'Sale Point is required.';
    if (entryType === 'order' && !form.order_status) return 'Order Status is required.';
    if (!form.location) return 'TWZ Location is required.';
    if (!form.vendor) return 'Vendor is required.';
    if (!form.artist.trim() || !form.artist_id) return 'Please select an Artist from the available options.';
    if (!form.venue.trim() || !form.venue_id) return 'Please select a Venue from the available options.';
    if (!form.ticket_platform) return 'Platform is required.';
    if (entryType === 'purchase' && !selectedUser?.user_id) return 'User is required.';
    if (!form.event_date) return 'Date is required.';
    if (!isValidNativeDate(form.event_date)) return 'Date must be a valid date.';
    if (!isAllowedManualEntryDate(form.event_date)) {
      return 'Invalid Date: Manual entries cannot be entered for past dates.';
    }

    const quantity = Number(form.quantity);
    if ((entryType === 'purchase' || entryType === 'order') && (!Number.isInteger(quantity) || quantity <= 0)) {
      return 'Quantity must be a whole number greater than zero.';
    }

    const amount = Number(form.amount_total);
    if (form.amount_total === '' || Number.isNaN(amount) || amount < 0) {
      return 'Amount Total is required.';
    }

    const unit = Number(form.unit_price);
    if (form.unit_price === '' || Number.isNaN(unit) || unit < 0) {
      return 'Unit Price is required.';
    }

    return '';
  };

  const buildManualPurchasePayload = (cardOverrides = {}) => ({
    entry_type: entryType,
    location: form.location,
    venue_id: form.venue_id,
    vendor: form.vendor,
    artist_id: form.artist_id,
    artist: form.artist.trim(),
    event_name: form.event_name.trim() || form.artist.trim(),
    event_date: toDisplayDate(form.event_date),
    ticket_type: form.ticket_type.trim() || null,
    quantity: entryType === 'void' ? 0 : Number(form.quantity),
    currency: currency || null,
    amount_total: Number(form.amount_total),
    unit_price: Number(form.unit_price),
    venue: form.venue.trim(),
    ticket_platform: form.ticket_platform,
    card: form.override_primary_card.trim() || null,
    card_id: selectedOverrideCard?.card_id || null,
    save_card_to_account: false,
    user_id: selectedUser?.user_id || null,
    ...cardOverrides,
  });

  const finishSuccessfulSave = (result) => {
    setMessage(`Manual ${entryType === 'void' ? 'void' : entryType} saved.`);
    setSavedLinks({
      orderId: result.item?.order_id || result.item?.tikwiz_order_id || null,
      purchaseId: result.purchase_id || null,
      sourceUuid: result.source_uuid || null,
      eventRef: toEventRef(result.source_uuid),
      eventId: result.event_id || null,
    });
    if (!keepDetails) {
      setForm(initialForm);
      setEntryType(initialEntryType);
      setAdvancedOptionsOpen(false);
      setSelectedOverrideCard(null);
      resetUser();
    }
    setShowCardRequiredModal(false);
    setSelectedRequiredCard(null);
    setRequiredCardQuery('');
    setSaveRequiredCardToAccount(false);
  };

  const handlePurchaseResult = (result) => {
    if (result.success) {
      finishSuccessfulSave(result);
      return;
    }
    if (result.code === 'CARD_REQUIRED') {
      setCardRequiredDetails(result.details || null);
      setSelectedRequiredCard(null);
      setRequiredCardQuery('');
      setRequiredCardSuggestions([]);
      setSaveRequiredCardToAccount(false);
      setShowCardRequiredModal(true);
      return;
    }
    setError(result.error);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');
    setSavedLinks(null);

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    const result = entryType === 'order'
      ? await transactionsService.createOrder({
          order_id: form.order_id.trim(),
          sale_point: form.sale_point,
          vendor: form.vendor,
          location: form.location,
          venue_id: form.venue_id,
          venue_name: form.venue.trim(),
          artist_id: form.artist_id,
          event_name: form.event_name.trim() || form.artist.trim(),
          day: parseNativeDate(form.event_date)?.toLocaleDateString(undefined, { weekday: 'long' }) || null,
          event_date: toDisplayDate(form.event_date),
          type_label: form.ticket_type.trim() || 'GA',
          quantity: Number(form.quantity),
          currency: currency || null,
          total: Number(form.amount_total),
          unit_price: Number(form.unit_price),
          status: form.order_status,
          operator: form.operator.trim() || null,
          order_date: new Date().toLocaleString(),
          platform: form.ticket_platform || null,
          notes: form.notes.trim() || null,
          user_id: selectedUser?.user_id || null,
        })
      : await eventsService.createManualPurchase(buildManualPurchasePayload());

    if (entryType === 'order') {
      if (result.success) finishSuccessfulSave(result);
      else setError(result.error);
    } else {
      handlePurchaseResult(result);
    }
    setSaving(false);
  };

  const closeCardRequiredModal = () => {
    if (saving) return;
    setShowCardRequiredModal(false);
    setSelectedRequiredCard(null);
    setRequiredCardQuery('');
    setRequiredCardSuggestions([]);
    setSaveRequiredCardToAccount(false);
  };

  const handleRequiredCardSubmit = async (event) => {
    event.preventDefault();
    if (!selectedRequiredCard?.card_id) {
      setError('Select or add a card to continue.');
      return;
    }

    setSaving(true);
    setError('');
    const result = await eventsService.createManualPurchase(buildManualPurchasePayload({
      card: selectedRequiredCard.card_name,
      card_id: selectedRequiredCard.card_id,
      save_card_to_account: saveRequiredCardToAccount,
    }));
    if (result.success && keepDetails) {
      setForm((current) => ({
        ...current,
        override_primary_card: selectedRequiredCard.card_name || current.override_primary_card,
      }));
      setSelectedOverrideCard(selectedRequiredCard);
    }
    handlePurchaseResult(result);
    setSaving(false);
  };

  const handleSavedUuidClick = () => {
    if (!savedLinks?.sourceUuid) return;
    const eventLookup = savedLinks.eventRef || savedLinks.sourceUuid;

    if (canOpenEvents) {
      navigate(`${eventsPath}?q=${encodeURIComponent(eventLookup)}`);
      return;
    }

    navigate(`/user/transactions/purchases?q=${encodeURIComponent(savedLinks.purchaseId || savedLinks.sourceUuid)}`);
  };

  return (
    <div className="manual-entry-page">
      <header className="manual-entry-header">
        <div className="manual-entry-header-content">
          <HeaderNewTabButton />
          <div>
            <h1>Manual Entry</h1>
            <p>Add purchase, void, and order records manually.</p>
          </div>
          <button className="btn btn-secondary" type="button" onClick={() => navigate(returnTo)}>
            {backLabel}
          </button>
        </div>
      </header>

      <main className="manual-entry-main">
        <section className="manual-entry-product-bar">
          <button
            className={`manual-entry-product-option ${entryType === 'purchase' ? 'active' : ''}`}
            type="button"
            onClick={() => switchEntryType('purchase')}
          >
            Purchase
          </button>
          <button
            className={`manual-entry-product-option ${entryType === 'void' ? 'active' : ''}`}
            type="button"
            onClick={() => switchEntryType('void')}
          >
            VOID
          </button>
          <button
            className={`manual-entry-product-option ${entryType === 'order' ? 'active' : ''}`}
            type="button"
            onClick={() => switchEntryType('order')}
          >
            Order
          </button>
        </section>

        <form className="manual-entry-form" onSubmit={handleSubmit}>
          <div className="manual-entry-form-header">
            <h2>{entryType === 'void' ? 'Void Details' : entryType === 'order' ? 'Order Details' : 'Purchase Details'}</h2>
            <span>Required fields are marked with *</span>
          </div>

          {error && <div className="notification error">{error}</div>}
          {message && (
            <div className="notification success manual-entry-success">
              <span>{message}</span>
              {savedLinks?.sourceUuid && (
                <button
                  type="button"
                  className="manual-entry-result-link"
                  title={canOpenEvents ? 'Open in Events' : 'Open related purchase search'}
                  onClick={handleSavedUuidClick}
                >
                  UUID: {savedLinks.sourceUuid}
                </button>
              )}
              {savedLinks?.purchaseId && (
                <button
                  type="button"
                  className="manual-entry-result-link"
                  onClick={() => navigate(`/user/transactions/purchases?q=${encodeURIComponent(savedLinks.purchaseId)}`)}
                >
                  Purchase ID: {savedLinks.purchaseId}
                </button>
              )}
              {savedLinks?.orderId && (
                <button
                  type="button"
                  className="manual-entry-result-link"
                  onClick={() => navigate(`/user/transactions/orders?q=${encodeURIComponent(savedLinks.orderId)}`)}
                >
                  Order ID: {savedLinks.orderId}
                </button>
              )}
            </div>
          )}

          <div className="manual-entry-grid">
            {entryType === 'order' && (
              <>
                <div className="form-group">
                  <label htmlFor="manual-sale-point">Sale Point *</label>
                  <select
                    id="manual-sale-point"
                    value={form.sale_point}
                    onChange={(event) => updateField('sale_point', event.target.value)}
                    disabled={saving}
                  >
                    {orderSalePointOptions.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="manual-order-id">Order ID *</label>
                  <input
                    id="manual-order-id"
                    type="text"
                    value={form.order_id}
                    onChange={(event) => updateField('order_id', event.target.value)}
                    placeholder="Marketplace order ID"
                    disabled={saving}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="manual-order-status">Status *</label>
                  <select
                    id="manual-order-status"
                    value={form.order_status}
                    onChange={(event) => updateField('order_status', event.target.value)}
                    disabled={saving}
                  >
                    {orderStatusOptions.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            <div className="form-group">
              <label htmlFor="manual-location">TWZ Location *</label>
              <select
                id="manual-location"
                value={form.location}
                onChange={(event) => updateField('location', event.target.value)}
                disabled={saving}
              >
                <option value="">Select TWZ location</option>
                {locationOptions.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group manual-entry-vendor-group">
              <label htmlFor="manual-vendor">Vendor *</label>
              <div className="manual-entry-vendor-dropdown" ref={vendorDropdownRef}>
                <button
                  id="manual-vendor"
                  className="manual-entry-vendor-trigger"
                  type="button"
                  onClick={() => {
                    if (saving) return;
                    setVendorMenuOpen((current) => !current);
                    setOtherVendorsOpen(false);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setVendorMenuOpen(true);
                    }
                  }}
                  disabled={saving}
                  aria-haspopup="menu"
                  aria-expanded={vendorMenuOpen}
                >
                  <span className={form.vendor ? '' : 'manual-entry-placeholder'}>{selectedVendorLabel}</span>
                  <span className="manual-entry-vendor-arrow manual-entry-vendor-arrow-down" aria-hidden="true"></span>
                </button>

                {vendorMenuOpen && (
                  <div className="manual-entry-vendor-menu" role="menu" aria-labelledby="manual-vendor">
                    {primaryVendorOptions.map((item) => (
                      <button
                        key={item.value}
                        className={`manual-entry-vendor-option ${form.vendor === item.value ? 'active' : ''}`}
                        type="button"
                        role="menuitem"
                        onClick={() => selectVendorOption(item.value)}
                      >
                        {item.label}
                      </button>
                    ))}

                    <div
                      className="manual-entry-vendor-submenu-shell"
                      onMouseEnter={() => setOtherVendorsOpen(true)}
                      onMouseLeave={() => setOtherVendorsOpen(false)}
                    >
                      <button
                        className={`manual-entry-vendor-option manual-entry-vendor-submenu-trigger ${otherVendorsOpen ? 'active-soft' : ''}`}
                        type="button"
                        role="menuitem"
                        onClick={() => setOtherVendorsOpen((current) => !current)}
                        onFocus={() => setOtherVendorsOpen(true)}
                        aria-haspopup="menu"
                        aria-expanded={otherVendorsOpen}
                      >
                        <span>Other Vendors</span>
                        <span className="manual-entry-vendor-arrow manual-entry-vendor-arrow-right" aria-hidden="true"></span>
                      </button>

                      {otherVendorsOpen && (
                        <div className="manual-entry-vendor-submenu" role="menu" aria-label="Other Vendors">
                          {otherVendorOptions.map((item) => (
                            <button
                              key={item.value}
                              className={`manual-entry-vendor-option ${form.vendor === item.value ? 'active' : ''}`}
                              type="button"
                              role="menuitem"
                              onClick={() => selectVendorOption(item.value)}
                            >
                              {item.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="form-group user-autocomplete-group">
              <label htmlFor="manual-user">User{entryType === 'purchase' ? ' *' : ''}</label>
              <input
                id="manual-user"
                type="text"
                value={userQuery}
                onChange={(event) => handleUserSearch(event.target.value)}
                placeholder={!form.vendor ? 'Select vendor first' : (currentVendorSearchKey ? 'Search vendor users' : 'Search all users')}
                disabled={saving || !form.vendor || entryType === 'void'}
                autoComplete="off"
              />
              {suggestionsOpen && (
                <div className="autocomplete-list">
                  {suggestionsLoading && (
                    <button type="button" className="autocomplete-item" disabled>
                      Searching...
                    </button>
                  )}
                  {!suggestionsLoading && suggestions.length === 0 && (
                    <button type="button" className="autocomplete-item" disabled>
                      No users found
                    </button>
                  )}
                  {!suggestionsLoading &&
                    suggestions.map((item) => (
                      <button
                        type="button"
                        key={item.user_id}
                        className="autocomplete-item"
                        onClick={() => {
                          setSelectedUser(item);
                          setUserQuery(item.username);
                          setSuggestionsOpen(false);
                        }}
                      >
                        <strong>{item.username}</strong>
                        <span>{item.email || item.name || ''}</span>
                      </button>
                    ))}
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="manual-date">Date *</label>
              <input
                id="manual-date"
                type="date"
                lang="en-GB"
                value={form.event_date}
                onChange={(event) => updateField('event_date', event.target.value)}
                disabled={saving}
                required
              />
            </div>

            <div className="form-group artist-autocomplete-group">
              <div className="manual-entry-field-label-row">
                <label htmlFor="manual-artist">Artist *</label>
              </div>
              <input
                id="manual-artist"
                type="text"
                value={form.artist}
                onChange={(event) => updateField('artist', event.target.value)}
                onFocus={() => {
                  if (!saving && !form.artist_id && form.artist.trim()) {
                    setArtistSuggestionsOpen(true);
                  }
                }}
                placeholder="Search artists"
                disabled={saving}
                autoComplete="off"
              />
              {artistSuggestionsOpen && (
                <div className="autocomplete-list">
                  {artistSuggestionsLoading && (
                    <button type="button" className="autocomplete-item" disabled>
                      Searching...
                    </button>
                  )}
                  {!artistSuggestionsLoading && form.artist.trim() && artistSuggestions.length === 0 && (
                    <button type="button" className="autocomplete-item" disabled>
                      No artists found
                    </button>
                  )}
                  {!artistSuggestionsLoading &&
                    artistSuggestions.map((item) => (
                      <button
                        type="button"
                        key={item.artist_id}
                        className="autocomplete-item"
                        onClick={() => selectArtist(item)}
                      >
                        <strong>{item.name}</strong>
                        <span>{`${item.event_count || 0} events | ${item.alias_count || 0} aliases`}</span>
                      </button>
                    ))}
                  {!artistSuggestionsLoading && (
                    <button
                      type="button"
                      className="autocomplete-item manual-entry-add-registry-option"
                      onClick={openCreateArtistModal}
                    >
                      <strong>Add New Artist</strong>
                      <span>{form.artist.trim() ? `Create "${form.artist.trim()}"` : 'Create an artist record'}</span>
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="form-group ticket-type-autocomplete-group">
              <label htmlFor="manual-ticket-type">Ticket Type</label>
              <input
                id="manual-ticket-type"
                type="text"
                value={form.ticket_type}
                onChange={(event) => updateField('ticket_type', event.target.value)}
                onFocus={openTicketTypeSuggestions}
                placeholder="GA, VIP, Before 1am..."
                disabled={saving}
                autoComplete="off"
              />
              {ticketTypeSuggestionsOpen && (
                <div className="autocomplete-list">
                  {ticketTypeSuggestions.length === 0 && (
                    <button type="button" className="autocomplete-item" disabled>
                      New ticket type
                    </button>
                  )}
                  {ticketTypeSuggestions.map((item) => (
                      <button
                        type="button"
                        key={item}
                        className="autocomplete-item"
                        onClick={() => {
                          skipNextTicketTypeSuggestions.current = true;
                          updateField('ticket_type', item);
                          setTicketTypeSuggestions([]);
                          setTicketTypeSuggestionsOpen(false);
                        }}
                      >
                        <strong>{item}</strong>
                      </button>
                    ))}
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="manual-quantity">Quantity *</label>
              <input
                id="manual-quantity"
                type="number"
                min={entryType === 'void' ? '0' : '1'}
                step="1"
                value={entryType === 'void' ? '0' : form.quantity}
                onChange={(event) => updateField('quantity', event.target.value)}
                disabled={saving || entryType === 'void'}
              />
            </div>

            <div className="form-group">
              <label htmlFor="manual-currency">Currency</label>
              {form.location === 'World' ? (
                <select
                  id="manual-currency"
                  value={currency}
                  onChange={(event) => updateField('currency', event.target.value)}
                  disabled={saving}
                  title="Select an approved currency for World"
                >
                  {APPROVED_CURRENCIES.map((code) => (
                    <option key={code} value={code}>{code}</option>
                  ))}
                </select>
              ) : (
                <input
                  id="manual-currency"
                  type="text"
                  value={currency}
                  readOnly
                  placeholder="Select TWZ location"
                  title="Currency is determined by TWZ Location"
                />
              )}
            </div>

            <div className="form-group">
              <label htmlFor="manual-amount-total">{entryType === 'order' ? 'Revenue Total *' : 'Amount Total *'}</label>
              <input
                id="manual-amount-total"
                type="number"
                min="0"
                step="0.01"
                value={form.amount_total}
                onChange={(event) => updateField('amount_total', event.target.value)}
                placeholder="0.00"
                disabled={saving || entryType === 'void'}
              />
            </div>

            <div className="form-group">
              <label htmlFor="manual-unit-price">{entryType === 'order' ? 'Sell Unit Price *' : 'Unit Price *'}</label>
              <input
                id="manual-unit-price"
                type="number"
                min="0"
                step="0.01"
                value={form.unit_price}
                onChange={(event) => updateField('unit_price', event.target.value)}
                placeholder="0.00"
                disabled={saving || entryType === 'void'}
              />
            </div>

            <div className="form-group venue-autocomplete-group">
              <div className="manual-entry-field-label-row">
                <label htmlFor="manual-venue">Venue *</label>
              </div>
              <input
                id="manual-venue"
                type="text"
                value={form.venue}
                onChange={(event) => updateField('venue', event.target.value)}
                onFocus={() => {
                  if (!saving && !form.venue_id) {
                    setVenueSuggestionsOpen(true);
                  }
                }}
                placeholder={form.location ? 'Search venues in TWZ location' : 'Search all venues'}
                disabled={saving}
                autoComplete="off"
              />
              {venueSuggestionsOpen && (
                <div className="autocomplete-list">
                  {venueSuggestionsLoading && (
                    <button type="button" className="autocomplete-item" disabled>
                      Searching...
                    </button>
                  )}
                  {!venueSuggestionsLoading && form.venue.trim() && venueSuggestions.length === 0 && (
                    <button type="button" className="autocomplete-item" disabled>
                      No venues found
                    </button>
                  )}
                  {!venueSuggestionsLoading &&
                    venueSuggestions.map((item) => (
                      <button
                        type="button"
                        key={item.venue_id}
                        className="autocomplete-item"
                        onClick={() => selectVenue(item)}
                      >
                        <strong>{item.name}</strong>
                        <span>{[item.location, item.country, item.twz_location].filter(Boolean).join(' | ')}</span>
                      </button>
                    ))}
                  {!venueSuggestionsLoading && (
                    <button
                      type="button"
                      className="autocomplete-item manual-entry-add-venue-option"
                      onClick={openCreateVenueModal}
                    >
                      <strong>Add New Venue</strong>
                      <span>{form.venue.trim() ? `Create "${form.venue.trim()}"` : 'Create a venue record'}</span>
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="manual-ticket-platform">Platform *</label>
              <select
                id="manual-ticket-platform"
                value={form.ticket_platform}
                onChange={(event) => updateField('ticket_platform', event.target.value)}
                disabled={saving}
              >
                <option value="">Select platform</option>
                {currentPlatformOptions.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>

            {entryType === 'order' && (
              <>
                <div className="form-group">
                  <label htmlFor="manual-operator">Operator</label>
                  <input
                    id="manual-operator"
                    type="text"
                    value={form.operator}
                    onChange={(event) => updateField('operator', event.target.value)}
                    placeholder="TS, Ria, DV..."
                    disabled={saving}
                  />
                </div>

                <div className="form-group manual-entry-notes-field">
                  <label htmlFor="manual-notes">Notes</label>
                  <input
                    id="manual-notes"
                    type="text"
                    value={form.notes}
                    onChange={(event) => updateField('notes', event.target.value)}
                    placeholder="Follow-up, confirmation, delivery notes..."
                    disabled={saving}
                  />
                </div>
              </>
            )}
          </div>

          <details
            className="manual-entry-advanced"
            open={advancedOptionsOpen}
            onToggle={(event) => setAdvancedOptionsOpen(event.currentTarget.open)}
          >
            <summary className="manual-entry-advanced-summary">
              <span>Optional Overrides</span>
              <span className="manual-entry-advanced-hint">Expand to add Event Name or Override Primary Card</span>
            </summary>

            <div className="manual-entry-advanced-grid">
              <div className="form-group card-autocomplete-group">
                <label htmlFor="manual-override-primary-card">Override Primary Card</label>
                <input
                  id="manual-override-primary-card"
                  type="text"
                  value={form.override_primary_card}
                  onChange={(event) => updateField('override_primary_card', event.target.value)}
                  onFocus={() => {
                    if (!saving && form.override_primary_card.trim()) {
                      setCardSuggestionsOpen(true);
                    }
                  }}
                  placeholder="Leave blank to use card on file"
                  disabled={saving}
                  autoComplete="off"
                />
                {cardSuggestionsOpen && (
                  <div className="autocomplete-list">
                    {cardSuggestionsLoading && (
                      <button type="button" className="autocomplete-item" disabled>
                        Searching...
                      </button>
                    )}
                    {!cardSuggestionsLoading && form.override_primary_card.trim() && cardSuggestions.length === 0 && (
                      <button type="button" className="autocomplete-item" disabled>
                        No cards found
                      </button>
                    )}
                    {!cardSuggestionsLoading &&
                      cardSuggestions.map((item) => (
                      <button
                        type="button"
                        key={item.card_id}
                        className="autocomplete-item"
                        onClick={() => selectOverrideCard(item)}
                      >
                        <strong>{item.card_name}</strong>
                        <span>{item.owner_username || item.status || ''}</span>
                      </button>
                      ))}
                    {!cardSuggestionsLoading && (
                      <button
                        type="button"
                        className="autocomplete-item manual-entry-add-registry-option"
                        onClick={() => openCreateCardModal('override')}
                      >
                        <strong>Add New Card</strong>
                        <span>{form.override_primary_card.trim() ? `Create "${form.override_primary_card.trim()}"` : 'Create a card record'}</span>
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="manual-event-name">Event Name</label>
                <input
                  id="manual-event-name"
                  type="text"
                  value={form.event_name}
                  onChange={(event) => updateField('event_name', event.target.value)}
                  placeholder="Defaults to artist unless changed"
                  disabled={saving}
                />
              </div>
            </div>
          </details>

          <label className="manual-entry-keep-details">
            <input
              type="checkbox"
              checked={keepDetails}
              onChange={(event) => setKeepDetails(event.target.checked)}
              disabled={saving}
            />
            <span>Keep Details</span>
          </label>

          <div className="manual-entry-actions">
            <button className="btn btn-secondary" type="button" onClick={() => navigate(returnTo)}>
              Cancel
            </button>
            <button className="btn btn-primary" type="submit" disabled={saving}>
              {saving ? 'Saving...' : entryType === 'order' ? 'Save Order' : entryType === 'void' ? 'Save Void' : 'Save Purchase'}
            </button>
          </div>
        </form>

        {showCreateArtistModal && (
          <div className="manual-entry-modal-backdrop" onMouseDown={closeCreateArtistModal}>
            <section className="manual-entry-modal" onMouseDown={(event) => event.stopPropagation()}>
              <div className="manual-entry-modal-header">
                <h2>Add Artist</h2>
                <button
                  className="manual-entry-modal-close"
                  type="button"
                  onClick={closeCreateArtistModal}
                  disabled={createArtistSaving}
                >
                  x
                </button>
              </div>

              <form className="manual-entry-modal-form manual-entry-artist-modal-form" onSubmit={handleCreateArtist}>
                <div className="form-group">
                  <label htmlFor="manual-create-artist-name">Artist Name</label>
                  <input
                    id="manual-create-artist-name"
                    type="text"
                    value={createArtistName}
                    onChange={(event) => setCreateArtistName(event.target.value)}
                    disabled={createArtistSaving}
                    required
                  />
                </div>

                {createArtistError && <div className="notification error">{createArtistError}</div>}

                <div className="manual-entry-modal-actions">
                  <button className="btn btn-secondary" type="button" onClick={closeCreateArtistModal} disabled={createArtistSaving}>
                    Cancel
                  </button>
                  <button className="btn btn-primary" type="submit" disabled={createArtistSaving}>
                    {createArtistSaving ? 'Saving...' : 'Save Artist'}
                  </button>
                </div>
              </form>
            </section>
          </div>
        )}

        {showCreateVenueModal && (
          <div className="manual-entry-modal-backdrop" onMouseDown={closeCreateVenueModal}>
            <section className="manual-entry-modal" onMouseDown={(event) => event.stopPropagation()}>
              <div className="manual-entry-modal-header">
                <h2>Add Venue</h2>
                <button
                  className="manual-entry-modal-close"
                  type="button"
                  onClick={closeCreateVenueModal}
                  disabled={createVenueSaving}
                >
                  x
                </button>
              </div>

              <form className="manual-entry-modal-form" onSubmit={handleCreateVenue}>
                <div className="form-group">
                  <label htmlFor="manual-create-venue-name">Venue Name</label>
                  <input
                    id="manual-create-venue-name"
                    type="text"
                    value={createVenueForm.name}
                    onChange={(event) => updateCreateVenueField('name', event.target.value)}
                    disabled={createVenueSaving}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="manual-create-venue-location">Location</label>
                  <input
                    id="manual-create-venue-location"
                    type="text"
                    value={createVenueForm.location}
                    onChange={(event) => updateCreateVenueField('location', event.target.value)}
                    disabled={createVenueSaving}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="manual-create-venue-country">Country</label>
                  <input
                    id="manual-create-venue-country"
                    type="text"
                    value={createVenueForm.country}
                    onChange={(event) => updateCreateVenueField('country', event.target.value)}
                    disabled={createVenueSaving}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="manual-create-venue-capacity">Capacity</label>
                  <input
                    id="manual-create-venue-capacity"
                    type="number"
                    min="0"
                    step="1"
                    value={createVenueForm.capacity}
                    onChange={(event) => updateCreateVenueField('capacity', event.target.value)}
                    disabled={createVenueSaving}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="manual-create-venue-twz-location">TWZ Location</label>
                  <select
                    id="manual-create-venue-twz-location"
                    value={createVenueForm.twz_location}
                    onChange={(event) => updateCreateVenueField('twz_location', event.target.value)}
                    disabled={createVenueSaving}
                  >
                    <option value="">Select...</option>
                    {locationOptions.map((item) => (
                      <option key={item} value={item}>{item}</option>
                    ))}
                  </select>
                </div>

                {createVenueError && <div className="notification error">{createVenueError}</div>}

                <div className="manual-entry-modal-actions">
                  <button className="btn btn-secondary" type="button" onClick={closeCreateVenueModal} disabled={createVenueSaving}>
                    Cancel
                  </button>
                  <button className="btn btn-primary" type="submit" disabled={createVenueSaving}>
                    {createVenueSaving ? 'Saving...' : 'Save Venue'}
                  </button>
                </div>
              </form>
            </section>
          </div>
        )}

        {showCardRequiredModal && (
          <div className="manual-entry-modal-backdrop" onMouseDown={closeCardRequiredModal}>
            <section className="manual-entry-modal" onMouseDown={(event) => event.stopPropagation()}>
              <div className="manual-entry-modal-header">
                <div>
                  <h2>Card Required</h2>
                  <p>{cardRequiredDetails?.message || 'Select or add a card to continue.'}</p>
                </div>
                <button className="manual-entry-modal-close" type="button" onClick={closeCardRequiredModal} disabled={saving}>x</button>
              </div>

              <form className="manual-entry-modal-form" onSubmit={handleRequiredCardSubmit}>
                <div className="form-group card-autocomplete-group">
                  <label htmlFor="manual-required-card">Card</label>
                  <input
                    id="manual-required-card"
                    type="text"
                    value={requiredCardQuery}
                    onChange={(event) => {
                      setRequiredCardQuery(event.target.value);
                      setSelectedRequiredCard(null);
                      setError('');
                    }}
                    placeholder="Search cards"
                    disabled={saving}
                    autoComplete="off"
                    required
                  />
                  {!!requiredCardQuery.trim() && !selectedRequiredCard && (
                    <div className="autocomplete-list manual-entry-modal-autocomplete-list">
                      {requiredCardSuggestionsLoading && <button type="button" className="autocomplete-item" disabled>Searching...</button>}
                      {!requiredCardSuggestionsLoading && requiredCardSuggestions.map((item) => (
                        <button type="button" key={item.card_id} className="autocomplete-item" onClick={() => selectRequiredCard(item)}>
                          <strong>{item.card_name}</strong>
                          <span>{item.owner_username || item.status || ''}</span>
                        </button>
                      ))}
                      {!requiredCardSuggestionsLoading && (
                        <button type="button" className="autocomplete-item manual-entry-add-registry-option" onClick={() => openCreateCardModal('required')}>
                          <strong>Add New Card</strong>
                          <span>{`Create "${requiredCardQuery.trim()}"`}</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {cardRequiredDetails?.can_save_to_account && (
                  <label className="manual-entry-checkbox-row">
                    <input
                      type="checkbox"
                      checked={saveRequiredCardToAccount}
                      onChange={(event) => setSaveRequiredCardToAccount(event.target.checked)}
                      disabled={saving}
                    />
                    Save as primary card for {selectedUser?.username || 'this user'}’s {selectedVendorLabel} account
                  </label>
                )}

                {error && <div className="notification error">{error}</div>}

                <div className="manual-entry-modal-actions">
                  <button className="btn btn-secondary" type="button" onClick={closeCardRequiredModal} disabled={saving}>Cancel</button>
                  <button className="btn btn-primary" type="submit" disabled={saving || !selectedRequiredCard?.card_id}>
                    {saving ? 'Saving...' : 'Save Purchase'}
                  </button>
                </div>
              </form>
            </section>
          </div>
        )}

        {showCreateCardModal && (
          <div className="manual-entry-modal-backdrop manual-entry-card-modal-backdrop" onMouseDown={closeCreateCardModal}>
            <section className="manual-entry-modal" onMouseDown={(event) => event.stopPropagation()}>
              <div className="manual-entry-modal-header">
                <h2>Add Card</h2>
                <button className="manual-entry-modal-close" type="button" onClick={closeCreateCardModal} disabled={createCardSaving}>x</button>
              </div>

              <form className="manual-entry-modal-form" onSubmit={handleCreateCard}>
                <div className="form-group">
                  <label htmlFor="manual-create-card-name">Card Name</label>
                  <input id="manual-create-card-name" value={createCardForm.card_name} onChange={(event) => updateCreateCardField('card_name', event.target.value)} disabled={createCardSaving} required />
                </div>
                <div className="form-group">
                  <label htmlFor="manual-create-card-owner">Owner</label>
                  <input id="manual-create-card-owner" value={createCardForm.owner_username} onChange={(event) => updateCreateCardField('owner_username', event.target.value)} disabled={createCardSaving} required />
                </div>
                <div className="form-group">
                  <label htmlFor="manual-create-card-status">Status</label>
                  <input id="manual-create-card-status" value={createCardForm.status} onChange={(event) => updateCreateCardField('status', event.target.value)} disabled={createCardSaving} />
                </div>
                <div className="form-group">
                  <label htmlFor="manual-create-card-expiry">Expiry</label>
                  <input id="manual-create-card-expiry" value={createCardForm.expiry} onChange={(event) => updateCreateCardField('expiry', event.target.value)} disabled={createCardSaving} placeholder="MM/YY" />
                </div>
                <div className="form-group">
                  <label htmlFor="manual-create-card-ccv">CCV</label>
                  <input id="manual-create-card-ccv" value={createCardForm.ccv} onChange={(event) => updateCreateCardField('ccv', event.target.value)} disabled={createCardSaving} />
                </div>
                <label className="manual-entry-checkbox-row">
                  <input type="checkbox" checked={createCardForm.is_zen_privacy} onChange={(event) => updateCreateCardField('is_zen_privacy', event.target.checked)} disabled={createCardSaving} />
                  Zen/Privacy card
                </label>

                {createCardError && <div className="notification error">{createCardError}</div>}

                <div className="manual-entry-modal-actions">
                  <button className="btn btn-secondary" type="button" onClick={closeCreateCardModal} disabled={createCardSaving}>Cancel</button>
                  <button className="btn btn-primary" type="submit" disabled={createCardSaving}>{createCardSaving ? 'Saving...' : 'Save Card'}</button>
                </div>
              </form>
            </section>
          </div>
        )}
      </main>
    </div>
  );
};

export default ManualEntryPage;
